/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { offlineSync } from '../../utils/offlineSync';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { 
  Play, 
  Pause, 
  History, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  Wallet, 
  Activity, 
  ChevronDown, 
  ChevronUp, 
  ShieldCheck, 
  UserCheck, 
  Info,
  Server,
  Briefcase,
  Coins,
  RotateCcw
} from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';

interface CompanyOperationsCardProps {
  lang: 'en' | 'ha';
  onStateChange?: () => void;
  driversCount: number;
  vehiclesCount: number;
  activeCycle?: any;
}

export const CompanyOperationsCard: React.FC<CompanyOperationsCardProps> = ({ 
  lang, 
  onStateChange,
  driversCount,
  vehiclesCount,
  activeCycle
}) => {
  const [opsState, setOpsState] = useState<any>({
    status: 'Setup Mode',
    currentCycle: '',
    currentDay: 1,
    startedBy: null,
    startedAt: null,
    pauseHistory: [],
    auditLog: []
  });
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await offlineSync.sync(api.request);
      if (onStateChange) onStateChange();
    } catch (err) {
      console.error("Sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const [metrics, setMetrics] = useState<any>({
    totalDrivers: 0,
    totalTricycles: 0,
    todayCollections: 0,
    companyWalletBalance: 0,
    systemHealth: 'Healthy'
  });
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseDays, setPauseDays] = useState<number | string>(2);
  const [cycleDays, setCycleDays] = useState<number>(30);
  const [actionError, setActionError] = useState('');
  const [checklist, setChecklist] = useState<any>([]);
  const [generatedCycleId, setGeneratedCycleId] = useState('');
  const [showCycleDebugInspector, setShowCycleDebugInspector] = useState(false);

  const getExtendedEndDate = () => {
    if (!activeCycle?.endDate) return 'N/A';
    const baseDate = new Date(activeCycle.endDate);
    const daysToAdd = parseInt(String(pauseDays), 10) || 0;
    baseDate.setDate(baseDate.getDate() + daysToAdd);
    return baseDate.toISOString().split('T')[0];
  };

  // Sync with Firestore activeCycle prop
  useEffect(() => {
    if (activeCycle) {
      setOpsState(prev => ({
        ...prev,
        status: activeCycle.status === 'paused' ? 'Paused' : (activeCycle.isActive ? 'Operational Mode' : 'Setup Mode'),
        currentCycle: activeCycle.cycleId || prev.currentCycle,
        currentDay: activeCycle.currentDay || 1
      }));
    }
  }, [activeCycle]);

  // Inline forms state
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [salaryRole, setSalaryRole] = useState('Admin');
  const [salaryAmount, setSalaryAmount] = useState('150000');
  
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [walletBalance, setWalletBalance] = useState('5000000');

  const fetchOperationsState = async () => {
    try {
      const res = await api.getOperationsState();
      if (res && res.success) {
        setOpsState(res.state);
        setMetrics(res.metrics);
      }
    } catch (err) {
      console.error("Failed to fetch company operations state:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperationsState();
    
    // Register event listener for real-time state changes via custom SSE DB dispatch
    const handleDbChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.company_operations_state) {
        setOpsState(detail.company_operations_state);
        if (detail.financials) {
          // Recalculate metrics based on detail
          const todayStr = new Date().toISOString().split('T')[0];
          const todayCol = (detail.driver_payments || [])
            .filter((p: any) => p.status === 'approved' && p.date && p.date.startsWith(todayStr))
            .reduce((sum, p: any) => sum + (parseFloat(p.amount) || 0), 0);

          setMetrics({
            totalDrivers: detail.drivers?.length || 0,
            totalTricycles: detail.vehicles?.length || 0,
            todayCollections: todayCol,
            companyWalletBalance: detail.company_settings?.wallet_balance || 0,
            systemHealth: 'Healthy'
          });
        }
      } else {
        fetchOperationsState();
      }
    };
    
    window.addEventListener('db-change', handleDbChange);
    return () => {
      window.removeEventListener('db-change', handleDbChange);
    };
  }, []);

  const evaluateChecklist = async () => {
    setActionError('');
    try {
      const stateRes = await api.getOperationsState();
      const currentDB = (window as any).lastSSEState || {};
      const companySettings = currentDB.company_settings || {};
      const drivers = currentDB.drivers || [];
      const vehicles = currentDB.vehicles || [];
      const shareholders = currentDB.shareholders || [];
      const users = currentDB.users || [];

      const adminCount = users.filter((u: any) => u.role_id === 'role-admin' || u.role_id === 'role-director' || u.role === 'admin' || u.role === 'director').length;

      const profileComplete = companySettings.companyName && companySettings.companyAddress && companySettings.phone && companySettings.email;
      const driverComplete = drivers.length >= 1;
      const tricycleComplete = vehicles.length >= 1 && vehicles.some((v: any) => v.driverId || v.driver_id);
      const shareholderComplete = shareholders.length >= 1;
      const salaryComplete = companySettings.salary_configured || (companySettings.salaries && companySettings.salaries.length >= 1);
      const walletComplete = companySettings.wallet_initialized || companySettings.wallet_balance !== undefined;
      const rulesComplete = companySettings.rules_shareholder_configured && companySettings.rules_cycle_configured;

      const items = [
        {
          id: 'profile',
          titleEn: 'Corporate Profile Configured',
          titleHa: 'Siffar Kamfani a Tsare',
          descEn: 'Requires company name, address, phone, and email.',
          descHa: 'Ana buƙatar sunan kamfani, adireshi, tarho, da imel.',
          isDone: !!profileComplete,
        },
        {
          id: 'admin',
          titleEn: 'Administrator Registered',
          titleHa: 'Akalla Akwai Mai Kula Guda Guda',
          descEn: 'Requires at least one Administrator or Executive Director account.',
          descHa: 'Ana buƙatar aƙalla asusun mai kula guda ɗaya.',
          isDone: adminCount >= 1,
        },
        {
          id: 'driver',
          titleEn: 'Drivers Registered',
          titleHa: 'Rijistar Direbobi',
          descEn: 'Requires at least one active driver registered in the system.',
          descHa: 'Ana buƙatar aƙalla direba guda ɗaya a cikin tsarin.',
          isDone: driverComplete,
        },
        {
          id: 'tricycle',
          titleEn: 'Tricycles Assigned',
          titleHa: 'Kekuna da aka raba wa Direbobi',
          descEn: 'Requires at least one registered tricycle assigned to an active driver.',
          descHa: 'Ana buƙatar aƙalla keke guda ɗaya da aka raba wa direba.',
          isDone: tricycleComplete,
        },
        {
          id: 'shareholder',
          titleEn: 'Shareholders Registered',
          titleHa: 'Rijistar Masu Sanya Jari',
          descEn: 'Requires at least one shareholder profile registered.',
          descHa: 'Ana buƙatar aƙalla mai sanya jari guda ɗaya.',
          isDone: shareholderComplete,
        },
        {
          id: 'salary',
          titleEn: 'Salary Configurations Complete',
          titleHa: 'Sari da Tsarin Albashi',
          descEn: 'Requires setting operational salaries for administrative roles.',
          descHa: 'Ana buƙatar saita albashin masu gudanarwa.',
          isDone: !!salaryComplete,
          action: () => setShowSalaryForm(true)
        },
        {
          id: 'wallet',
          titleEn: 'Company Wallet Initialized',
          titleHa: 'Asusun Kamfani (Wallet)',
          descEn: 'Requires initializing company financial balance sheet.',
          descHa: 'Ana buƙatar buɗe asusun kuɗi na kamfani.',
          isDone: !!walletComplete,
          action: () => setShowWalletForm(true)
        },
        {
          id: 'rules',
          titleEn: 'Operational Rules Configured',
          titleHa: 'Dokokin Gudanarwa da Zagaye',
          descEn: 'Requires 30-Day operating cycle rules and shareholder investment ratio setup.',
          descHa: 'Ana buƙatar tsarin zagaye na kwanaki 30 da dokokin raba riba.',
          isDone: !!rulesComplete,
          action: async () => {
            try {
              await api.configRules({ rules_shareholder_configured: true, rules_cycle_configured: true, roles_configured: true });
              evaluateChecklist();
            } catch (err: any) {
              setActionError(err.message);
            }
          }
        }
      ];

      setChecklist(items);
    } catch (e) {
      console.error(e);
    }
  };

  const handleStartCompanyOperations = async () => {
    setActionError('');
    try {
      const res = await api.startOperations({ 
        cycleId: generatedCycleId,
        durationDays: cycleDays
      });
      if (res && res.success) {
        setOpsState(res.state);
        setShowChecklistModal(false);
        if (res.detail) {
          window.dispatchEvent(new CustomEvent('db-change', { detail: res.detail }));
        }

        if (onStateChange) onStateChange();
        fetchOperationsState();
      }
    } catch (err: any) {
      setActionError(err.message || 'Validation failed. Ensure checklist is 100% complete.');
    }
  };

  const handlePauseCompanyOperations = async () => {
    if (!pauseReason) {
      setActionError(lang === 'en' ? 'Reason is required to suspend operations.' : 'Ana buƙatar bayanin dalili.');
      return;
    }
    setActionError('');
    try {
      const res = await api.pauseOperations({ reason: pauseReason, pauseDays: Number(pauseDays) || 1 });
      if (res && res.success) {
        setOpsState(res.state);
        setShowPauseModal(false);
        setPauseReason('');
        if (res.detail) {
          window.dispatchEvent(new CustomEvent('db-change', { detail: res.detail }));
        }

        if (onStateChange) onStateChange();
        fetchOperationsState();
      }
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleDeleteCycle = async () => {
    setActionError('');
    try {
      const targetId = opsState?.currentCycle || 'active';
      const res = await api.deleteCycle(targetId);
      if (res && res.success) {
        setShowDeleteModal(false);
        if (onStateChange) onStateChange();
        fetchOperationsState();
      }
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete cycle.');
    }
  };

  const handleResumeCompanyOperations = async () => {
    setActionError('');
    try {
      const res = await api.resumeOperations('Resumed by administrator');
      if (res && res.success) {
        setOpsState(res.state);
        if (res.detail) {
          window.dispatchEvent(new CustomEvent('db-change', { detail: res.detail }));
        }

        if (onStateChange) onStateChange();
        fetchOperationsState();
      }
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleSaveSalary = async () => {
    try {
      await api.configSalaries([{ role: salaryRole, amount: parseFloat(salaryAmount) }]);
      setShowSalaryForm(false);
      evaluateChecklist();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleSaveWallet = async () => {
    try {
      await api.configWallet(parseFloat(walletBalance));
      setShowWalletForm(false);
      evaluateChecklist();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleOpenChecklist = async () => {
    evaluateChecklist();
    try {
      const res = await api.request('/api/director/cycles').catch(() => ({ cycles: [] }));
      const existingCycles = res?.cycles || [];
      let maxNum = 0;
      for (const c of existingCycles) {
        if (c && c.id) {
          const matches = c.id.match(/\d+/g);
          if (matches) {
            const numStr = matches[matches.length - 1];
            const num = parseInt(numStr, 10);
            if (!isNaN(num) && num > maxNum) {
              maxNum = num;
            }
          }
        }
      }
      const nextNum = maxNum > 0 ? maxNum + 1 : 2459;
      const padded = String(nextNum).padStart(4, '0');
      setGeneratedCycleId(`CYC-2026-${padded}`);
    } catch (err) {
      setGeneratedCycleId('');
    }
    setShowChecklistModal(true);
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-900 border border-slate-800 rounded-xl p-6 h-28 w-full flex items-center justify-center">
        <span className="text-xs text-slate-500 font-mono">Syncing enterprise state engine...</span>
      </div>
    );
  }

  // Determine styling based on status
  const isSetup = (opsState?.status || 'Setup Mode') === 'Setup Mode';
  const isPaused = opsState?.status === 'Paused';
  const isOperational = opsState?.status === 'Operational Mode';

  let borderStyle = 'border-amber-500/30 bg-amber-950/10';
  let badgeStyle = 'bg-amber-500/10 text-amber-500 border-amber-500/20';
  if (isOperational) {
    borderStyle = 'border-emerald-500/30 bg-emerald-950/10';
    badgeStyle = 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
  } else if (isPaused) {
    borderStyle = 'border-rose-500/30 bg-rose-950/10';
    badgeStyle = 'bg-rose-500/10 text-rose-500 border-rose-500/20';
  }

  return (
    <div id="company-operations-container" className="w-full flex flex-col gap-2">
      {/* Primary operations banner card */}
      <Card className={`relative overflow-hidden border p-3 sm:p-4 transition-all duration-300 rounded-xl shadow-md backdrop-blur-md ${borderStyle}`}>
        {/* Glow effect at background */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 rounded-full blur-2xl opacity-10 bg-primary-gold" />
        
        <div className="flex flex-col gap-2 z-10 relative">
          
          {/* Status block */}
          <div className="flex items-start gap-2.5">
            <div className={`p-1.5 rounded-lg border flex items-center justify-center shrink-0 ${
              isSetup ? 'bg-amber-500/15 border-amber-500/20 text-amber-500' :
              isPaused ? 'bg-rose-500/15 border-rose-500/20 text-rose-500' :
              'bg-emerald-500/15 border-emerald-500/20 text-emerald-500'
            }`}>
              <Activity className={`h-4.5 w-4.5 ${isOperational ? 'animate-pulse' : ''}`} />
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-[8px] sm:text-[9px] uppercase tracking-widest text-text-muted font-bold">
                  {lang === 'en' ? 'ENTERPRISE SYSTEM STATE' : 'TSARIN GUDANAR DA SASHIN AIKI'}
                </span>
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="p-1 rounded hover:bg-slate-200 transition-colors"
                  title="Force Sync Data"
                >
                  <RotateCcw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                </button>
                <span className={`px-1.5 py-0.2 rounded-full text-[7px] sm:text-[8px] font-extrabold uppercase tracking-wider border ${badgeStyle}`}>
                  {opsState?.status || 'Setup Mode'}
                </span>
              </div>
              <h3 className="text-xs sm:text-sm font-black text-text-main tracking-tight mt-0.5 leading-none">
                {isSetup && (lang === 'en' ? 'Setup & Staging Phase' : 'Matakin Shirya Kayan Aiki')}
                {isOperational && (lang === 'en' ? 'Live Fleet Operations Active' : 'Rukunin Keken Napep Na Kan Hanyar Aiki')}
                {isPaused && (lang === 'en' ? 'Corporate Suspension Active' : 'An Dakatar da Ayyuka Na Dan Lokaci')}
              </h3>
              <p className="text-[9px] sm:text-[10px] text-text-muted max-w-sm leading-tight mt-0.5">
                {isSetup && (lang === 'en' ? 'Configure drivers, assign tricycles, and finalize wallet setups safely before live operations.' : 'Sanya direbobi, kekuna, da asusun wallet kafin a fara aiki.')}
                {isOperational && (lang === 'en' ? 'Auto remittance logs, fuel vouchers, and profit allocation models are fully live.' : 'Ana lissafin kudaden remittance da rabon riba na kwanaki 30 a raye.')}
                {isPaused && (lang === 'en' ? 'System locked. Installments are frozen and drivers cannot submit transactions.' : 'An dakatar da lissafi, sanya kudi, da duk wani aiki na asusu har sai an dawo da shi.')}
              </p>
            </div>
          </div>

          {/* Quick Metrics & Action Area */}
          <div className="flex flex-col gap-2 w-full mt-1 border-t border-brand-gold/10 pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 w-full">
              {/* Operating Cycle stats */}
              {!isSetup && (
                <div className="flex items-center gap-3 bg-card-bg/40 border border-border-main/30 rounded-lg py-1 px-2.5 text-center shrink-0">
                  <div className="text-left">
                    <span className="text-[7.5px] uppercase tracking-wider text-text-muted block font-semibold leading-none">{lang === 'en' ? 'Cycle' : 'Zagaye'}</span>
                    <div className="text-[10px] font-black text-text-main flex items-center gap-1 mt-0.5 leading-none">
                      <Clock className="h-2.5 w-2.5 text-brand-gold" />
                      {activeCycle?.cycleId || opsState?.currentCycle || 'N/A'}
                    </div>
                  </div>
                  <div className="border-l border-border-main/30 pl-2 text-left">
                    <span className="text-[7.5px] uppercase tracking-wider text-text-muted block font-semibold leading-none">{lang === 'en' ? 'Cycle Day' : 'Rana'}</span>
                    <span className="text-[10px] font-black text-text-main mt-0.5 block leading-none">
                      Day {activeCycle?.currentDay || opsState?.currentDay || 1}/{activeCycle?.totalCycleDays || 30}
                    </span>
                  </div>
                </div>
              )}

              {/* State Controls */}
              <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end w-full">
                {isSetup && (
                  <Button 
                    id="btn-start-ops"
                    onClick={handleOpenChecklist}
                    className="flex-1 max-w-[180px] bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black flex items-center justify-center gap-1 py-1 px-2.5 rounded-lg shadow-sm transition-all duration-200 cursor-pointer text-[9px] sm:text-[10px]"
                  >
                    <Play className="h-3 w-3 fill-slate-950" />
                    {lang === 'en' ? 'Start Operations' : 'Fara Gudanarwa'}
                  </Button>
                )}

                {isOperational && (
                  <Button 
                    id="btn-pause-ops"
                    onClick={() => { setActionError(''); setShowPauseModal(true); }}
                    className="flex-1 max-w-[120px] bg-rose-600/10 hover:bg-rose-600/20 text-rose-500 border border-rose-600/30 font-bold flex items-center justify-center gap-1 py-1 px-2 rounded-lg cursor-pointer text-[9px] sm:text-[10px]"
                  >
                    <Pause className="h-3 w-3" />
                    {lang === 'en' ? 'Pause' : 'Dakatar'}
                  </Button>
                )}

                {isPaused && (
                  <Button 
                    id="btn-resume-ops"
                    onClick={handleResumeCompanyOperations}
                    className="flex-1 max-w-[120px] bg-emerald-500 text-slate-950 hover:bg-emerald-600 font-extrabold flex items-center justify-center gap-1 py-1 px-2 rounded-lg border-b border-emerald-700 cursor-pointer text-[9px] sm:text-[10px]"
                  >
                    <Play className="h-3 w-3 fill-slate-950" />
                    {lang === 'en' ? 'Resume' : 'Ci Gaba'}
                  </Button>
                )}

                <Button 
                  variant="outline"
                  onClick={() => setShowHistory(!showHistory)}
                  className="px-1.5 py-1 rounded-lg border border-border-main hover:bg-card-bg/80 text-text-muted font-bold text-[9px] sm:text-[10px] flex items-center justify-center gap-1 cursor-pointer shrink-0"
                >
                  <History className="h-3 w-3" />
                  {showHistory ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Small live metrics grid underneath for immediate visibility */}
        <div className="grid grid-cols-4 gap-1 border-t border-border-main/30 mt-2 pt-1.5 text-[9px] sm:text-[10px] leading-tight">
          <div className="flex flex-col items-center justify-center p-0.5 sm:p-1 bg-slate-500/5 rounded">
            <span className="text-[6.5px] sm:text-[7px] uppercase text-text-muted font-bold block">{lang === 'en' ? 'Drivers' : 'Direbobi'}</span>
            <span className="text-xs font-black text-text-main">{metrics.totalDrivers || driversCount}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-0.5 sm:p-1 bg-slate-500/5 rounded">
            <span className="text-[6.5px] sm:text-[7px] uppercase text-text-muted font-bold block">{lang === 'en' ? 'Fleet' : 'Kekuna'}</span>
            <span className="text-xs font-black text-text-main">{metrics.totalTricycles || vehiclesCount}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-0.5 sm:p-1 bg-slate-500/5 rounded">
            <span className="text-[6.5px] sm:text-[7px] uppercase text-text-muted font-bold block">{lang === 'en' ? "Remits" : 'Remittance'}</span>
            <span className="text-xs font-black text-text-main">₦{(metrics.todayCollections || 0).toLocaleString()}</span>
          </div>
          <div className="flex flex-col items-center justify-center p-0.5 sm:p-1 bg-slate-500/5 rounded">
            <span className="text-[6.5px] sm:text-[7px] uppercase text-text-muted font-bold block">{lang === 'en' ? 'Health' : 'Lafiya'}</span>
            <span className="text-[9px] sm:text-[10px] font-black text-cyan-500">{metrics.systemHealth}</span>
          </div>
        </div>

        {/* Cycle Management Flow Debug Inspector / Overlay */}
        <div className="mt-2 pt-2 border-t border-slate-700/40 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowCycleDebugInspector(!showCycleDebugInspector)}
              className="text-[10px] font-mono font-bold text-amber-400 hover:text-amber-300 flex items-center gap-1 bg-amber-500/10 hover:bg-amber-500/20 px-2 py-1 rounded-lg border border-amber-500/20 transition-all cursor-pointer"
            >
              <Activity className="h-3 w-3" />
              {showCycleDebugInspector ? 'Hide Cycle Debug Inspector' : '🛠️ Open Cycle Debug Inspector (Raw Data & End Date)'}
            </button>
            <span className="text-[9px] font-mono text-slate-400">
              {activeCycle?.extendedDays ? `Extended by +${activeCycle.extendedDays} days` : 'No extension days'}
            </span>
          </div>

          {showCycleDebugInspector && (() => {
            const startStr = activeCycle?.startDate || '';
            const endStr = activeCycle?.endDate || '';
            const extension = activeCycle?.totalPausedSeconds ? Math.floor(activeCycle.totalPausedSeconds / 86400) : 0;

            return (
              <div className="bg-slate-950 border border-amber-500/30 rounded-xl p-3.5 text-xs font-mono text-slate-200 flex flex-col gap-3 animate-fadeIn">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <span className="text-amber-400 font-black uppercase text-[10px] tracking-wider">
                    🔍 Cycle Management Flow Debug Inspector (Canonical)
                  </span>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px]">
                    Verified: Canonical Backend Data
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                  <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-800">
                    <span className="text-[9px] text-slate-400 font-bold uppercase block mb-1">Calculated End Date Verification</span>
                    <div className="space-y-1 text-slate-300">
                      <div><span className="text-slate-500">Start Date:</span> <span className="text-amber-300">{startStr}</span></div>
                      <div><span className="text-slate-500">Base Duration:</span> <span className="text-slate-200">30 Days</span></div>
                      <div><span className="text-slate-500">Total Paused:</span> <span className="text-cyan-400 font-bold">~{extension} Days</span></div>
                      <div className="border-t border-slate-800 pt-1 mt-1">
                        <span className="text-slate-500">Canonical End Date:</span> <span className="text-emerald-400 font-black">{endStr}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-800 flex flex-col">
                    <span className="text-[9px] text-slate-400 font-bold uppercase block mb-1">Raw Canonical Status (API)</span>
                    <pre className="text-[9px] text-emerald-300 overflow-x-auto whitespace-pre-wrap flex-1 bg-slate-950 p-2 rounded border border-slate-800/80 max-h-36">
                      {JSON.stringify(activeCycle, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </Card>

      {/* Expandable operations history log */}
      {showHistory && (
        <Card className="border border-border-main bg-card-bg/65 backdrop-blur-md rounded-xl p-5 shadow-inner animate-fadeIn">
          <div className="flex justify-between items-center pb-3 border-b border-border-main/50 mb-3">
            <h4 className="text-xs font-black text-text-main uppercase tracking-wider flex items-center gap-1.5">
              <History className="h-4 w-4 text-primary-gold" />
              {lang === 'en' ? 'Operations Audit Trail (Immutable)' : 'Tarihin Sauye-sauyen Aiki (Baya Goguwa)'}
            </h4>
            <span className="text-[10px] font-mono text-text-muted">ROOT_NODE_SECURE</span>
          </div>

          {(!opsState?.auditLog || opsState.auditLog.length === 0) ? (
            <div className="text-center py-6 text-text-muted text-xs font-mono">
              {lang === 'en' ? 'No operational phase shifts recorded yet.' : 'Babu tarihin sauya matakin aiki tukunna.'}
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-64 overflow-y-auto pr-1">
              {(opsState?.auditLog || []).map((log: any, idx: number) => (
                <div key={log.id || idx} className="p-3 rounded-lg bg-bg-base/80 border border-border-main/50 flex flex-col sm:flex-row justify-between sm:items-center gap-3 text-xs">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                        log.action.includes('Start') ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/25' :
                        log.action.includes('Pause') ? 'bg-rose-500/10 text-rose-500 border border-rose-500/25' :
                        'bg-blue-500/10 text-blue-500 border border-blue-500/25'
                      }`}>
                        {log.action}
                      </span>
                      <span className="font-extrabold text-text-main">{log.user}</span>
                    </div>
                    <p className="text-text-muted mt-1 leading-normal italic">
                      "{log.reason}"
                    </p>
                  </div>
                  <div className="text-right flex flex-col items-start sm:items-end gap-0.5 font-mono text-[10px] text-text-muted">
                    <span>{new Date(log.timestamp).toLocaleString()}</span>
                    <span>IP: {log.ip || '127.0.0.1'} | {log.browser}</span>
                    <span className="text-[8px] truncate max-w-[150px]">{log.device}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
           {/* Streamlined Launch Activation Modal - No More Setup, only Start & End dates */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full p-6 shadow-2xl relative flex flex-col">
            
            <div className="flex justify-between items-start pb-3 border-b border-slate-800">
              <div>
                <h3 className="text-base font-black text-slate-100 flex items-center gap-2">
                  <Play className="h-4.5 w-4.5 fill-amber-500 text-amber-500" />
                  {lang === 'en' ? 'Start Operating Cycle' : 'Fara Zagayen Aiki'}
                </h3>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {lang === 'en' ? 'Bypassing setup checklist. Ready to deploy live operations immediately.' : 'Tsallake matakan saiti. An shirya tsaf domin fara ayyuka.'}
                </p>
              </div>
              <button 
                onClick={() => { setShowChecklistModal(false); setActionError(''); }}
                className="text-slate-400 hover:text-slate-200 cursor-pointer text-xl"
              >
                &times;
              </button>
            </div>

            {actionError && (
              <div className="my-3 p-3 bg-rose-500/10 border border-rose-500/25 text-rose-400 text-xs rounded-lg flex items-start gap-2 animate-shake">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{actionError}</span>
              </div>
            )}

            {/* Start and Scheduled End Dates display */}
            <div className="py-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <span className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wider block">
                    {lang === 'en' ? 'START DATE' : 'RANAR FARA'}
                  </span>
                  <span className="font-extrabold text-amber-400 font-mono text-xs mt-1 block">
                    {new Date().toISOString().split('T')[0]}
                  </span>
                </div>
                
                <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <span className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wider block">
                    {lang === 'en' ? 'SCHEDULED END' : 'RANAR KAMMALAWA'}
                  </span>
                  <span className="font-extrabold text-emerald-400 font-mono text-xs mt-1 block">
                    {new Date(Date.now() + cycleDays * 24 * 3600 * 1000).toISOString().split('T')[0]}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                <label className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wider block">
                  {lang === 'en' ? 'CYCLE DURATION (DAYS)' : 'TSAWON ZAGAYE (KWANAKI)'}
                </label>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={cycleDays}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCycleDays(isNaN(val) ? 30 : Math.max(1, val));
                  }}
                  className="font-extrabold text-amber-400 font-mono text-sm mt-1 bg-transparent border-b border-slate-800 focus:border-amber-400 focus:outline-none w-full"
                />
              </div>

              <div className="p-3 rounded-lg bg-slate-950 border border-slate-800 flex justify-between items-center">
                <div className="flex-1 mr-2">
                  <label className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wider block">
                    {lang === 'en' ? 'CYCLE IDENTIFIER (UNIQUE ID)' : 'LAMBAR SHIGA ZAGAYE (ID)'}
                  </label>
                  <input
                    type="text"
                    required
                    value={generatedCycleId}
                    onChange={(e) => setGeneratedCycleId(e.target.value)}
                    className="font-extrabold text-amber-400 font-mono text-sm mt-1 bg-transparent border-b border-slate-800 focus:border-amber-400 focus:outline-none w-full"
                  />
                </div>
                <div className="text-[9px] text-slate-500 italic font-medium shrink-0">
                  {lang === 'en' ? 'Sequential Serial' : 'Jerin Serial'}
                </div>
              </div>

              <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg text-[10px] text-slate-400 leading-normal">
                {lang === 'en' 
                  ? `Confirming will activate real-time tracking, open the financial center ledger, and start the high-precision ${cycleDays}-day operating cycle timer instantly.`
                  : `Tabbatarwa zai fara kidayar lokaci na kwanaki ${cycleDays} da lissafin kudaden shiga nan take.`}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-800 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => { setShowChecklistModal(false); setActionError(''); }}
                className="px-3 py-1.5 border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer text-[11px]"
              >
                {lang === 'en' ? 'Cancel' : 'Soke'}
              </Button>
              <Button 
                onClick={handleStartCompanyOperations}
                className="px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-slate-950 font-black flex items-center gap-1 rounded shadow-lg border-b-2 border-emerald-700 cursor-pointer text-[11px]"
              >
                <Play className="h-3 w-3 fill-slate-950 text-slate-950" />
                {lang === 'en' ? 'Confirm & Start Live' : 'Tabbatar & Fara Aiki'}
              </Button>
            </div>

          </div>
        </div>
      )}

      {/* Pause Operations Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full p-6 shadow-2xl relative">
            <h3 className="text-lg font-black text-slate-100 flex items-center gap-2">
              <Pause className="h-5 w-5 text-rose-500" />
              {lang === 'en' ? 'Confirm Operations Suspension' : 'Dakatar da Gudanar da Kamfani'}
            </h3>
            <p className="text-xs text-slate-400 mt-2">
              {lang === 'en' ? 'Suspension freezes installments, restricts new payouts, halts interest, and places a safety freeze across the system. Fully auditing-logged.' : 'Dakatarwa zai tsayar da duk wani lissafi da remittan kudi da sauran ayyuka na asusu.'}
            </p>

            {actionError && (
              <div className="my-3 p-3 bg-rose-500/10 border border-rose-500/25 text-rose-400 text-xs rounded-lg">
                {actionError}
              </div>
            )}

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 block font-bold mb-1">
                  {lang === 'en' ? 'Reason for Suspension (Immutable Audited Comment)' : 'Dalilin Dakatarwa'}
                </label>
                <textarea 
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder={lang === 'en' ? "E.g., Mid-year financial alignment checks" : "Sanya takaitaccen dalili a nan"}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-200 focus:outline-none focus:border-rose-500 h-20"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 block font-bold mb-1">
                  {lang === 'en' ? 'Extension Duration (Enter custom number of days)' : 'Kwanakin Kara Tsawon Zagaye'}
                </label>
                <input 
                  type="number"
                  min="1"
                  value={pauseDays}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setPauseDays("");
                    } else {
                      const num = parseInt(value, 10);
                      setPauseDays(Math.max(1, isNaN(num) ? 1 : num));
                    }
                  }}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-xs text-amber-400 font-mono font-bold focus:outline-none focus:border-rose-500"
                />
                <div className="bg-slate-950/50 border border-emerald-500/20 rounded-lg p-3 mt-2">
                  <p className="text-xs text-emerald-400 font-semibold italic">
                    {lang === 'en' 
                      ? `Cycle end date will be extended to ${getExtendedEndDate()} (+${pauseDays} day(s)) automatically.`
                      : `Ranar karewa zata kasance ${getExtendedEndDate()} (+kwanaki ${pauseDays}).`}
                  </p>
                  <p className="text-[10px] text-emerald-600 mt-1">
                    {lang === 'en' 
                      ? 'This will adjust all pending payment schedules and installment deadlines for all active fleet tricycles. This action is fully audited.' 
                      : 'Wannan zai gyara dukkan jadawalin biyan kuɗi da wa\'adin ragi ga dukkan tricycles dake aiki. Wannan mataki yana da cikakken bayani.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <Button 
                variant="outline" 
                onClick={() => { setShowPauseModal(false); setPauseReason(''); setActionError(''); }}
                className="px-4 py-2 border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer text-xs"
              >
                {lang === 'en' ? 'Cancel' : 'Soke'}
              </Button>
              <Button 
                onClick={handlePauseCompanyOperations}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-slate-100 font-extrabold rounded shadow-md cursor-pointer text-xs"
              >
                {lang === 'en' ? 'Suspend Operations' : 'Dakatar da Aiki'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Cycle Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-xl max-w-md w-full p-6 shadow-2xl relative">
            <h3 className="text-lg font-black text-rose-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
              {lang === 'en' ? 'Delete Operating Cycle Everywhere' : 'Goge Zagayen Aiki'}
            </h3>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">
              {lang === 'en' 
                ? `Are you sure you want to permanently delete cycle ${activeCycle?.cycleId || opsState?.currentCycle || 'Active Cycle'}? It will be removed globally from all dashboards.` 
                : `Shin kana son goge zagayen aiki ${activeCycle?.cycleId || opsState?.currentCycle || 'Zagayen Aiki'}? Zai goge daga ko'ina.`}
            </p>

            {actionError && (
              <div className="my-3 p-3 bg-rose-500/10 border border-rose-500/25 text-rose-400 text-xs rounded-lg">
                {actionError}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <Button 
                variant="outline" 
                onClick={() => { setShowDeleteModal(false); setActionError(''); }}
                className="px-4 py-2 border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer text-xs"
              >
                {lang === 'en' ? 'Cancel' : 'Soke'}
              </Button>
              <Button 
                onClick={handleDeleteCycle}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-slate-100 font-extrabold rounded shadow-md cursor-pointer text-xs"
              >
                {lang === 'en' ? 'Confirm Delete Everywhere' : 'Goge Daga Ko\'ina'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
